import React from 'react';
import { useTranslation } from 'react-i18next';
import { OrderItem } from '../../../api/types';
import { formatDateForDisplay } from '../../../utils';

// Per-locker open/close status derived from the two "last" timestamps on the
// rental item (Phase 1 of docs/plans/usage-event-log-foundation.md). This is
// the seed of the ActivityTimeline: once the event_log backend exists, the
// derivation below is replaced by real events while the header slot stays.
// Wording constraint: "open" is stamped when the backend dispatches the unlock
// command (not a confirmed physical opening), "closed" is device-confirmed by
// the monitoring cron — the labels must not claim more than that.
/** `timeZone`: the booking location's zone (#3219); the station default when omitted. */
const LockerAccessLog = ({ items, timeZone }: { items: OrderItem[], timeZone?: string }) => {
    const { t } = useTranslation();

    // A locker is only assigned at rental start; before that there is nothing
    // to report (matches the previous behavior of hiding the line entirely).
    const rows = items
        .filter((item) => item.rental?.id && item.doors?.length)
        .map((item) => {
            // Defensive shape guard: only ISO strings count as present. The
            // backend serializer turns unlisted Date fields into {} (see
            // processPGData / #1568) — any non-string value must degrade to
            // "absent", never reach the date formatter.
            const openedRaw = item.rental?.lastLockerOpened;
            const closedRaw = item.rental?.lastLockerClosed;
            const opened = typeof openedRaw === 'string' ? openedRaw : null;
            const closed = typeof closedRaw === 'string' ? closedRaw : null;
            const doorLabel = (item.doors ?? []).map((d) => d.door).join(', ');

            let status: string;
            if (!opened) {
                status = t('booking_details.locker_log.no_open');
            } else if (!closed || new Date(closed) < new Date(opened)) {
                status = t('booking_details.locker_log.open_no_close', {
                    openedAt: formatDateForDisplay(opened, timeZone),
                });
            } else {
                status = t('booking_details.locker_log.open_closed', {
                    openedAt: formatDateForDisplay(opened, timeZone),
                    closedAt: formatDateForDisplay(closed, timeZone),
                });
            }

            return { id: item.id, doorLabel, status };
        });

    if (!rows.length) {
        return null;
    }

    return (
        <>
            {rows.map((row) => (
                <p key={row.id} className={'text-sm text-gray-500 mb-1'}>
                    {t('booking_details.locker_log.locker', { door: row.doorLabel })}:{' '}
                    <span className={'font-bold'}>{row.status}</span>
                </p>
            ))}
        </>
    );
};

export default LockerAccessLog;
